// Firebase AI Logic — Gemini integration.
//
// Uses the Google AI (Gemini Developer API) backend. Requests are automatically
// protected by App Check (initialised in config/firebase), so no model API key
// is ever handled in the client. Keep any load-bearing AI (moderation, scoring)
// on the server; this client-side path is for low-stakes, user-facing helpers
// like conversation openers.

import { getAI, getGenerativeModel, VertexAIBackend } from 'firebase/ai';
import app from '@config/firebase';
import { User } from '../types/user.types';

// Vertex AI backend bills through the project's Google Cloud (Blaze) account —
// the same billing the Cloud Functions already use — rather than the separate
// AI Studio (Gemini Developer API) prepay credits, which were depleted.
// Requires the Vertex AI API to be enabled on the project (the Firebase AI
// Logic setup enables it). Default region is us-central1.
const ai = getAI(app, { backend: new VertexAIBackend() });

const icebreakerModel = getGenerativeModel(ai, {
  // gemini-2.5-flash is cheap + fast — ideal for short, high-volume helpers.
  model: 'gemini-2.5-flash',
  systemInstruction: [
    'You help two people who just matched on RoomZa, a South African roommate-finding app, start chatting.',
    'Write short, warm, specific opening messages the FIRST person can send to the SECOND.',
    'Rules: first-person voice ("I"), one sentence each, under 140 characters, friendly and casual',
    '(South African English is welcome). Reference something they genuinely share — an interest,',
    'lifestyle, neighbourhood, or budget fit — when the details allow. Never mention surnames,',
    'contact details, or anything not provided. Avoid emojis unless they feel natural. No numbering.',
    'Return ONLY a JSON array of exactly 3 strings.',
  ].join(' '),
  generationConfig: {
    responseMimeType: 'application/json',
    temperature: 1.1,
    maxOutputTokens: 400,
  },
});

const firstName = (name?: string) => name?.trim().split(/\s+/)[0] ?? 'there';

// Turn "2026-08-01" into "August 2026"; leave unparseable values alone.
const formatMoveDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
};

interface SharedContext {
  city?: string;
  lifestyle?: string[];
}

// Fallbacks used when generation fails — still grounded in the real profile
// (city, preferred area, shared interest, move-in date) so they never feel
// generic, and never dead-end the UI.
function buildFallbacks(
  other: User | null | undefined,
  shared: SharedContext | undefined,
  sharedInterests: string[],
): string[] {
  const city = shared?.city || other?.city;
  const hood = other?.neighborhoods?.[0];
  const interest = sharedInterests[0] || other?.interests?.[0];
  const moveDate = formatMoveDate(other?.moveInDate);

  let areaOpener = 'Hey! We matched — what part of town are you hoping to live in?';
  if (hood) {
    const aroundCity = city ? ` around ${city}` : '';
    areaOpener = `Hey! We matched — are you set on ${hood}, or open to other spots${aroundCity}?`;
  } else if (city) {
    areaOpener = `Hey! We matched — what part of ${city} are you hoping to live in?`;
  }

  const interestOpener = interest
    ? `Hi! I saw you're into ${interest.toLowerCase()} too — how do you usually spend your weekends?`
    : 'Hi! Your profile looks like a great fit — what does your ideal home vibe look like?';

  const moveOpener = moveDate
    ? `Hey there! Keen to find a place together — are you still aiming to move around ${moveDate}?`
    : 'Hey there! Keen to find a place together — when are you hoping to move?';

  return [areaOpener, interestOpener, moveOpener];
}

/**
 * Generate 3 personalised conversation openers the current user can send to a
 * new match. Falls back to generic openers if the model call fails.
 */
export async function generateIcebreakers(
  me: User | null | undefined,
  other: User | null | undefined,
  shared?: SharedContext,
): Promise<string[]> {
  const sharedInterests = (me?.interests ?? []).filter((i) => (other?.interests ?? []).includes(i));
  const fallbacks = buildFallbacks(other, shared, sharedInterests);
  if (!other) return fallbacks;

  const sharedLifestyle =
    shared?.lifestyle && shared.lifestyle.length > 0
      ? shared.lifestyle
      : (me?.lifestyle ?? []).filter((l) => (other.lifestyle ?? []).includes(l));

  const prompt = [
    `I am ${firstName(me?.name)}. I just matched with ${firstName(other.name)}.`,
    other.occupation ? `They work as a ${other.occupation}.` : '',
    shared?.city || other.city ? `We're both looking in ${shared?.city || other.city}.` : '',
    other.neighborhoods?.length ? `Areas they like: ${other.neighborhoods.slice(0, 3).join(', ')}.` : '',
    other.moveInDate ? `They want to move around ${formatMoveDate(other.moveInDate)}.` : '',
    sharedInterests.length ? `Shared interests: ${sharedInterests.join(', ')}.` : '',
    sharedLifestyle.length ? `Shared lifestyle: ${sharedLifestyle.join(', ')}.` : '',
    other.interests?.length ? `Their interests: ${other.interests.join(', ')}.` : '',
    'Write 3 openers I could send them. Reference the concrete details above (area, interests, move-in) rather than generic small talk.',
  ]
    .filter(Boolean)
    .join(' ');

  try {
    const result = await icebreakerModel.generateContent(prompt);
    const parsed = parseStringArray(result.response.text());
    return parsed.length ? parsed.slice(0, 3) : fallbacks;
  } catch (err) {
    console.error('Icebreaker generation failed:', err);
    return fallbacks;
  }
}

// Parse a JSON array of strings, tolerating markdown fences or a plain list.
function parseStringArray(text: string): string[] {
  const cleaned = text
    .trim()
    .replace(/^```(?:json)?/i, '')
    .replace(/```$/, '')
    .trim();

  try {
    const data = JSON.parse(cleaned);
    if (Array.isArray(data)) {
      return data
        .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
        .map((s) => s.trim());
    }
  } catch {
    // Not valid JSON — fall back to splitting lines.
  }

  return cleaned
    .split('\n')
    .map((line) => line.replace(/^[-*\d.\s"]+/, '').replace(/",?$/, '').trim())
    .filter(Boolean);
}
